// @ts-check
/**
 * Chart datafeed while tastytrade is connected: `TASTYTRADE:` tickers (tastytrade's own stocks) get tastytrade
 * candles over DXLink; every other ticker goes to BetterTrader's UDF datafeed exactly as without the connection.
 */
(function tastytradeDataFeedClosure() {
  'use strict';

  const TASTYTRADE_RESOLUTIONS = ['1', '5', '15', '30', '60', '240', '1D', '1W', '1M'];
  const DAY_MS = 864e5;
  const STREAM_RETRY_MS = 10000;
  // Chart resolution -> dxFeed candle period, and how far back the chart may page before it is told there is no more.
  const TASTYTRADE_PERIODS = {
    1: { period: 'm', lookback: 7 * DAY_MS },
    5: { period: '5m', lookback: 30 * DAY_MS },
    15: { period: '15m', lookback: 60 * DAY_MS },
    30: { period: '30m', lookback: 60 * DAY_MS },
    60: { period: 'h', lookback: 180 * DAY_MS },
    240: { period: '4h', lookback: 365 * DAY_MS },
    D: { period: 'd', lookback: 10 * 365 * DAY_MS },
    '1D': { period: 'd', lookback: 10 * 365 * DAY_MS },
    W: { period: 'w', lookback: 20 * 365 * DAY_MS },
    '1W': { period: 'w', lookback: 20 * 365 * DAY_MS },
    M: { period: 'mo', lookback: 20 * 365 * DAY_MS },
    '1M': { period: 'mo', lookback: 20 * 365 * DAY_MS },
  };

  /**
   * @implements {TradingView.IBasicDataFeed}
   */
  class TastytradeDataFeed {
    /**
     * @param {TradingView.IBasicDataFeed} udf - BetterTrader's datafeed
     * @param {Pick<ecapp.ITastytradeApiService, 'getDxLink' | 'getSymbolInfo' | 'checkTastytradeTicker'>} tastytrade
     */
    constructor(udf, tastytrade) {
      this.udf = udf;
      this.tastytrade = tastytrade;
      /** @type {Record<string, (() => void) | 'udf' | Object>} listenerGuid -> stop function, 'udf', or a stream still opening */
      this.subscriptions = {};
    }

    /**
     * @param {TradingView.OnReadyCallback} callback
     */
    onReady(callback) {
      this.udf.onReady(callback);
    }

    /**
     * @param {string} userInput
     * @param {string} exchange
     * @param {string} symbolType
     * @param {TradingView.SearchSymbolsCallback} onResult
     */
    searchSymbols(userInput, exchange, symbolType, onResult) {
      this.udf.searchSymbols(userInput, exchange, symbolType, onResult);
    }

    /**
     * @param {string} symbolName
     * @param {TradingView.ResolveCallback} onResolve
     * @param {TradingView.ErrorCallback} onError
     * @param {TradingView.SymbolResolveExtension} [extension]
     */
    resolveSymbol(symbolName, onResolve, onError, extension) {
      if (!this.tastytrade.checkTastytradeTicker(symbolName))
        return this.udf.resolveSymbol(symbolName, onResolve, onError, extension);

      const symbol = symbolName.slice(symbolName.indexOf(':') + 1);
      Promise.resolve(this.tastytrade.getSymbolInfo(symbol))
        .then((instrument) => onResolve(prepareSymbolInfo(symbolName, symbol, instrument)))
        .catch((error) => onError((error && error.message) || 'Unknown symbol'));
    }

    /**
     * @param {TradingView.LibrarySymbolInfo} symbolInfo
     * @param {TradingView.ResolutionString} resolution
     * @param {TradingView.PeriodParams} periodParams - seconds
     * @param {TradingView.HistoryCallback} onResult
     * @param {TradingView.ErrorCallback} onError
     */
    getBars(symbolInfo, resolution, periodParams, onResult, onError) {
      if (!this.tastytrade.checkTastytradeTicker(symbolInfo.ticker)) {
        return this.udf.getBars(symbolInfo, resolution, periodParams, onResult, onError);
      }

      const options = TASTYTRADE_PERIODS[resolution];
      if (!options) return onError('Unsupported resolution');

      const from = periodParams.from * 1000;
      const to = periodParams.to * 1000;

      this.tastytrade
        .getDxLink()
        .getCandles(getStreamerSymbol(symbolInfo), options.period, from)
        .then((candles) => {
          const bars = candles.filter((candle) => candle.time < to);
          // An empty range may just be a weekend; stop paging back only when there is nothing at all or it went too far.
          const isHistoryEnd = !bars.length && (!candles.length || from < Date.now() - options.lookback);
          onResult(bars, { noData: isHistoryEnd });
        })
        .catch((error) => onError((error && error.message) || 'tastytrade candles failed'));
    }

    /**
     * @param {TradingView.LibrarySymbolInfo} symbolInfo
     * @param {TradingView.ResolutionString} resolution
     * @param {TradingView.SubscribeBarsCallback} onTick
     * @param {string} listenerGuid
     * @param {() => void} onResetCacheNeededCallback
     */
    subscribeBars(symbolInfo, resolution, onTick, listenerGuid, onResetCacheNeededCallback) {
      if (!this.tastytrade.checkTastytradeTicker(symbolInfo.ticker)) {
        this.subscriptions[listenerGuid] = 'udf';
        return this.udf.subscribeBars(symbolInfo, resolution, onTick, listenerGuid, onResetCacheNeededCallback);
      }

      const options = TASTYTRADE_PERIODS[resolution];
      if (!options) return;

      // TradingView reuses a guid per ticker and resolution, so a stale request must not touch a newer one's slot.
      const request = {};
      this.subscriptions[listenerGuid] = request;
      this.openStream(listenerGuid, request, getStreamerSymbol(symbolInfo), options.period, onTick);
    }

    /**
     * Retries while the chart still holds `request`'s slot; a chart whose stream failed would otherwise look live.
     *
     * @param {string} listenerGuid
     * @param {Object} request - token of this subscribe call
     * @param {string} symbol - tastytrade streamer symbol
     * @param {string} period - dxFeed period
     * @param {TradingView.SubscribeBarsCallback} onTick
     */
    openStream(listenerGuid, request, symbol, period, onTick) {
      this.tastytrade
        .getDxLink()
        .subscribeCandles(symbol, period, onTick)
        .then((stop) => {
          if (this.subscriptions[listenerGuid] === request) this.subscriptions[listenerGuid] = stop;
          else stop();
        })
        .catch((error) => {
          console.error('tastytrade live candles', error);
          setTimeout(() => {
            if (this.subscriptions[listenerGuid] === request) {
              this.openStream(listenerGuid, request, symbol, period, onTick);
            }
          }, STREAM_RETRY_MS);
        });
    }

    /**
     * @param {string} listenerGuid
     */
    unsubscribeBars(listenerGuid) {
      const subscription = this.subscriptions[listenerGuid];
      delete this.subscriptions[listenerGuid];

      if (subscription === 'udf') this.udf.unsubscribeBars(listenerGuid);
      // A stream still opening stops itself once it finds its slot gone.
      else if (typeof subscription === 'function') subscription();
    }

    /**
     * @param {(time: number) => void} callback
     */
    getServerTime(callback) {
      if (this.udf.getServerTime) this.udf.getServerTime(callback);
    }
  }

  /**
   * @param {TradingView.LibrarySymbolInfo} symbolInfo
   * @return {string} dxFeed symbol: the equity's `streamer-symbol` when tastytrade gave one
   */
  function getStreamerSymbol(symbolInfo) {
    return /** @type {*} */ (symbolInfo).streamerSymbol || symbolInfo.name;
  }

  /**
   * @param {string} ticker - `TASTYTRADE:AAPL`
   * @param {string} symbol - `AAPL`
   * @param {*} instrument - tastytrade instrument from btTastytradeApiService.getSymbolInfo
   * @return {TradingView.LibrarySymbolInfo}
   */
  function prepareSymbolInfo(ticker, symbol, instrument) {
    const raw = (instrument && instrument.raw) || {};

    return /** @type {*} */ ({
      name: symbol,
      ticker: ticker,
      full_name: ticker,
      description: (instrument && instrument.description) || symbol,
      type: 'stock',
      exchange: 'tastytrade',
      listed_exchange: 'tastytrade',
      // Candles include pre-market and overnight trading; a 09:30-16:00 session would hide them.
      session: '24x7',
      timezone: 'America/New_York',
      format: 'price',
      minmov: 1,
      // The adapter derives the precision from tastytrade's tick sizes; stocks trade in cents by default.
      pricescale: Math.pow(10, instrument ? instrument.precision : 2),
      has_intraday: true,
      has_daily: true,
      has_weekly_and_monthly: true,
      has_empty_bars: false,
      supported_resolutions: TASTYTRADE_RESOLUTIONS,
      visible_plots_set: 'ohlcv',
      volume_precision: 0,
      data_status: 'streaming',
      streamerSymbol: raw['streamer-symbol'] || symbol,
    });
  }

  window.TastytradeDataFeed = TastytradeDataFeed;
})();
